import { CircularProgress, FormControl, FormControlLabel, Radio, RadioGroup, styled } from '@material-ui/core';
import { Checkbox, Typography, useConfirmationModal } from '@room-match/shared-ui-components'; // Remove Close Icon and Styled Dialog
import CloseIcon from 'shared/components/Icon/CloseIcon'; // Add talent app level folder CloseIcon and Styled Dialog
import StyledDialog from 'shared/elements/StyledDialog/StyledDialog';
import React, { useEffect, useState } from 'react';
import { FACIAL_HAIR } from 'shared/constants/FACIAL_HAIR';
import { IDENTICAL_TWIN_OR_TRIPLET } from 'shared/constants/IDENTICAL_TWIN_OR_TRIPLET';
import { PHYSICAL_ATTRIBUTES } from 'shared/constants/PHYSICAL_ATTRIBUTES';
import { PIERCINGS } from 'shared/constants/PIERCINGS';
import { TATTOOS } from 'shared/constants/TATTOOS';
import { DefinitionTitle } from 'shared/enums';
import { IGeneralSection } from 'shared/interfaces/IGeneral';
import { humanize } from 'shared/utils/humanizeText';
import {
  CheckboxContainer,
  CloseButton,
  Content,
  CustomButton,
  CustomDialogContent,
  FooterContainer,
  Header,
  TwoColumn,
} from './DefinitionModals.styles';
import { talentService } from 'shared/services/talentService';
import { useQueryClient } from 'react-query';

type Props = {
  definition_title: DefinitionTitle;
  open: boolean;
  onClose: (instance: boolean) => void;
  generalInfo: IGeneralSection;
  setInfo: (info: IGeneralSection) => void;
  saving: boolean;
};

const arrTitlesForRadioGroup = [DefinitionTitle.Tattoo, DefinitionTitle.TwinTriplet];

const FullWidthFormControl = styled(FormControl)(() => ({
  width: '100%',
}));

const GridRadioGroup = styled(RadioGroup)(() => ({
  display: 'grid',
  gridTemplateColumns: 'auto auto',
}));

const { getGeneral, updateTalent } = talentService();
const DefinitionModals: React.FC<Props> = ({
  definition_title = '—',
  open,
  onClose,
  setInfo,
  generalInfo: genInfo,
}) => {
  const [dataSet, handleDataSet] = useState<string[]>([]);
  const [checked, setChecked] = useState<string[]>([]);
  const { isOpen, handleOpen, ConfirmationModal, handleSetMessage } = useConfirmationModal({ type: 'warning' });
  const [value, setValue] = React.useState<DefinitionTitle | string>('');
  const { data: generalData, isLoading: isGeneralLoading } = getGeneral();
  const { mutateAsync: talentUpdate, isLoading: isGeneralSaving } = updateTalent();
  const queryClient = useQueryClient();
  const [generalInfo, setGeneralInfo] = useState<IGeneralSection>({
    ethnic_appearance_primary: '',
    ethnic_appearance_secondary: '',
    ethnic_appearance_other: '',
    hair_colour: '',
    eye_colour: '',
    hair_length: '',
    tattoo: [],
    piercings: [],
    facial_hair: [],
    physical_attributes: [],
    twin_triplet: [],
  });

  useEffect(() => {
    const genData = generalData?.data.attributes.general;

    if (genData !== null) {
      const gendata = {
        ethnic_appearance_other: genData?.ethnic_appearance_other || '',
        ethnic_appearance_primary: genData?.ethnic_appearance_primary || '',
        ethnic_appearance_secondary: genData?.ethnic_appearance_secondary || '',
        hair_colour: genData?.hair_colour || '',
        hair_length: genData?.hair_length || '',
        eye_colour: genData?.eye_colour || '',
        tattoo: genData?.tattoo || [],
        piercings: genData?.piercings || [],
        facial_hair: genData?.facial_hair || [],
        physical_attributes: genData?.physical_attributes || [],
        twin_triplet: genData?.twin_triplet || [],
      };

      setGeneralInfo((i) => ({
        ...i,
        ...gendata,
      }));
    }
  }, [generalData]);

  useEffect(() => {
    if (definition_title) {
      switch (definition_title) {
        case DefinitionTitle.FacialHair:
          handleDataSet(FACIAL_HAIR);
          setChecked(generalInfo.facial_hair || []);
          break;
        case DefinitionTitle.Piercings:
          handleDataSet(PIERCINGS);
          setChecked(generalInfo.piercings || []);
          break;
        case DefinitionTitle.PhysicalAttributes:
          handleDataSet(PHYSICAL_ATTRIBUTES);
          setChecked(generalInfo.physical_attributes || []);
          break;
        default:
          break;
      }
    }

    if (arrTitlesForRadioGroup.indexOf(definition_title as DefinitionTitle) !== -1) {
      const title = generalInfo[definition_title as DefinitionTitle.Tattoo | DefinitionTitle.TwinTriplet];
      const checkTitle = title && title.length !== 0 ? title[0] : '';
      setValue(checkTitle);
      setChecked([checkTitle]);
    }
  }, [definition_title, generalInfo, setValue]);

  const handleChecked = (label: string) => {
    if (checked.indexOf(label) > -1) {
      setChecked((c) => c.filter((f) => f !== label));
    } else {
      setChecked((c) => [...c, label]);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value;
    setValue(value);
    setChecked([value]);
  };

  const handleSave = async () => {
    const talentPayload = {
      general: {
        ...generalInfo,
        [definition_title]: checked,
      },
    };
    await talentUpdate(
      {
        ...talentPayload,
      },
      {
        onSuccess: async () => {
          await queryClient.resetQueries(['talents/general']), onClose(false);
          setInfo({
            ...genInfo,
            [definition_title]: checked,
          });
        },
      },
    );
  };

  const onExit = () => {
    const dataCopy: any = generalInfo;
    const origData = dataCopy[definition_title];
    if (origData.toString() !== checked.toString()) {
      handleSetMessage('Exit Confirmation', 'You have unsaved changes. Are you sure you want to exit?');
      handleOpen();
    } else {
      onClose(false);
    }
  };

  const renderRadioGroup = () => (
    <FullWidthFormControl>
      <GridRadioGroup
        aria-labelledby="demo-controlled-radio-buttons-group"
        name="controlled-radio-buttons-group"
        value={value}
        onChange={handleChange}
      >
        {definition_title === DefinitionTitle.Tattoo ? (
          <>
            {TATTOOS.map((el) => (
              <FormControlLabel value={el} control={<Radio color="primary" />} label={el} />
            ))}
          </>
        ) : null}
        {definition_title === DefinitionTitle.TwinTriplet ? (
          <>
            {IDENTICAL_TWIN_OR_TRIPLET.map((el) => (
              <FormControlLabel value={el} control={<Radio color="primary" />} label={el} />
            ))}
          </>
        ) : null}
      </GridRadioGroup>
    </FullWidthFormControl>
  );

  const renderCheckboxGroup = () => (
    <>
      <TwoColumn>
        {dataSet.map((i) => (
          <CheckboxContainer>
            <Checkbox
              value={checked.indexOf(i) > -1}
              label={i}
              name="default"
              labelPlacement="end"
              onChange={() => handleChecked(i)}
            />
          </CheckboxContainer>
        ))}
      </TwoColumn>
    </>
  );

  return (
    <StyledDialog open={open} onClose={onExit} maxWidth="sm" fullWidth aria-labelledby="simple-dialog-title">
      <CustomDialogContent>
        <CloseButton>
          <CloseIcon style={{ width: '14px' }} onClick={onExit} />
        </CloseButton>
        <Header>
          <p style={{ fontSize: 24 }}>{humanize(definition_title)}</p>
          <p style={{ wordBreak: 'normal', fontSize: 16 }}>Please select relevant options below</p>
        </Header>
        <Content>
          {arrTitlesForRadioGroup.indexOf(definition_title as DefinitionTitle) !== -1
            ? renderRadioGroup()
            : renderCheckboxGroup()}
        </Content>
        <FooterContainer>
          <CustomButton fullWidth onClick={handleSave}>
            {isGeneralLoading ? <CircularProgress style={{ width: 20, height: 20, color: 'white' }} /> : 'Save'}
          </CustomButton>
          <Typography fontSize={14}>
            This won't be visible on your profile, but you'll appear in searches based on what you've listed
          </Typography>
        </FooterContainer>
      </CustomDialogContent>
      {isOpen && <ConfirmationModal onConfirm={() => onClose(false)} />}
    </StyledDialog>
  );
};

export default DefinitionModals;
